import { Box, Button, Card, ChoiceList, EmptySearchResult, IndexTable, IndexFilters, Layout, Page, useSetIndexFiltersMode, Spinner, TextField, Text, useBreakpoints } from '@shopify/polaris';
import { ViewIcon } from '@shopify/polaris-icons';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { formatDateToUTC } from '#client/utils/helpers';
import ActivityLog from '#client/components/ActivityLog';
import { purchaseOrderService } from '#client/services/purchaseOrderServices.js';
import CustomBadge from '#client/components/common/CustomBadge/CustomBadge';
import StoreLocation from '../../components/StoreLocation';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import PURCHASE_ORDER_STRINGS from '#client/pages/PurchaseOrders/constants/purchaseOrders.en.json';
import './PurchaseOrdersDashboard.css';
import { queryClient } from '#client/utils/queryClient.js';

// Status options configuration for filtering and display
const STATUS_OPTIONS = [
    { label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PENDING_RECEIPT, value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PENDING_RECEIPT },
    { label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CLOSED, value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CLOSED },
];

// Column configuration
const COLUMN_CONFIG = [
    { key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER_TITLE, sortable: true },
    { key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR_TITLE, sortable: true },
    { key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION_TITLE, sortable: true },
    { key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS_TITLE, sortable: true },
    { key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_TITLE, sortable: true },
    { key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_TITLE, sortable: true },
    { key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_TITLE, sortable: true },
    { key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_TITLE, sortable: true },
    { key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_TITLE, sortable: true },
    { key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_TITLE, sortable: true },

    { key: PURCHASE_ORDER_STRINGS.ACTIONS, title: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ACTIONS_TITLE, sortable: false },
];

// Sort options for the purchase orders table
const SORT_OPTIONS = [
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER_ASC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASCENDING,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER_DESC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESCENDING,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR_ASC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.AZ,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR_DESC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ZA,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION_ASC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.AZ,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION_DESC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ZA,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS_ASC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.AZ,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS_DESC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ZA,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_ASC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.OLDEST_FIRST,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_DESC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.NEWEST_FIRST,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_ASC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.OLDEST_FIRST,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_DESC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.NEWEST_FIRST,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_ASC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.OLDEST_FIRST,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_DESC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.NEWEST_FIRST,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_ASC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASCENDING,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_DESC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESCENDING,
    },

    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_ASC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASCENDING,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_DESC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESCENDING,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_ASC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASCENDING,
    },
    {
        label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_TITLE,
        value: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_DESC_VALUE,
        directionLabel: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESCENDING,
    },
];

// Custom hook for debounced values
const useDebounce = (value) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [value, 500]);

    return debouncedValue;
};

export default function PurchaseOrdersDashboard() {
    const hasMounted = useRef(false);
    const navigate = useNavigate();
    const { currentUser } = useContext(SessionContext);

    // ====== UTILITY FUNCTIONS ======

    const resourceName = {
        singular: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PURCHASE_ORDER,
        plural: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PURCHASE_ORDERS,
    };
    const itemsPerPage = PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ITEMS_PER_PAGE;

    // Track the previous selected location to detect changes
    //const prevSelectedLocationRef = useRef(currentUser?.activeStore);

    // ====== SESSION STORAGE HELPERS ======
    const loadFiltersFromSession = () => {
        try {
            const raw = sessionStorage.getItem(PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.FILTER_STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch (err) {
            console.warn('Failed to parse filters from session:', err);
            return null;
        }
    };

    const saveFiltersToSession = (filters) => {
        try {
            sessionStorage.setItem(PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.FILTER_STORAGE_KEY, JSON.stringify(filters));
        } catch (err) {
            console.error('Error saving filters to sessionStorage:', err);
        }
    };

    // ====== INITIALIZE STATE FROM SESSION STORAGE ======
    const [savedFilters, setSavedFilters] = useState(loadFiltersFromSession());

    useEffect(() => {
        setSavedFilters(loadFiltersFromSession());
    }, []);

    // ====== SORT STATE ======
    const [sortSelected, setSortSelected] = useState(() => {
        const savedSort = savedFilters?.sortSelected;
        const first = Array.isArray(savedSort) ? savedSort[0] : null;

        const isValid = first && SORT_OPTIONS.some((opt) => opt.value === first);
        const fallback = [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_ASC_VALUE];

        const initialSort = isValid ? savedSort : fallback;

        return initialSort;
    });

    // ====== PAGINATION STATE ======
    const [currentPage, setCurrentPage] = useState(savedFilters?.currentPage || 1);

    useEffect(() => {
        if (savedFilters?.currentPage && savedFilters.currentPage !== currentPage) {
            setCurrentPage(savedFilters.currentPage);
        }
    }, [savedFilters]);

    // ====== DATA STATE ======
    const [dataState, setDataState] = useState({
        rows: [],
        total: 0,
        isLoading: true,
    });

    // ====== FILTER STATE ======
    const [queryValue, setQueryValue] = useState(savedFilters?.queryValue || '');
    const [poNumberFilter, setPoNumberFilter] = useState(savedFilters?.poNumberFilter || '');
    const [vendorFilter, setVendorFilter] = useState(() => {
        const selectedFilters = savedFilters?.vendorFilter;

        if (Array.isArray(selectedFilters)) {
            return selectedFilters;
        } else if (selectedFilters) {
            return [selectedFilters];
        }

        return [];
    });
    const [statusFilter, setStatusFilter] = useState(savedFilters?.statusFilter || [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PENDING_RECEIPT]); // Default status to "Pending Receipt" on initial load
    const [locationFilter, setLocationFilter] = useState(() => {
        const selectedFilters = savedFilters?.locationFilter;

        if (Array.isArray(selectedFilters)) {
            return selectedFilters;
        } else if (selectedFilters) {
            return [selectedFilters];
        }

        return [];
    });
    const [startDateFilter, setStartDateFilter] = useState(savedFilters?.startDateFilter || '');
    const [endDateFilter, setEndDateFilter] = useState(savedFilters?.endDateFilter || '');
    const [expectedShipDateFromFilter, setExpectedShipDateFromFilter] = useState(savedFilters?.expectedShipDateFromFilter || '');
    const [expectedShipDateToFilter, setExpectedShipDateToFilter] = useState(savedFilters?.expectedShipDateToFilter || '');
    const [cancelDateFromFilter, setCancelDateFromFilter] = useState(savedFilters?.cancelDateFromFilter || '');
    const [cancelDateToFilter, setCancelDateToFilter] = useState(savedFilters?.cancelDateToFilter || '');
    const [qtyOrderedMin, setQtyOrderedMin] = useState(savedFilters?.qtyOrderedMin ?? '');
    const [qtyOrderedMax, setQtyOrderedMax] = useState(savedFilters?.qtyOrderedMax ?? '');
    const [qtyReceivedMin, setQtyReceivedMin] = useState(savedFilters?.qtyReceivedMin ?? '');
    const [qtyReceivedMax, setQtyReceivedMax] = useState(savedFilters?.qtyReceivedMax ?? '');
    const [qtyReceivedInDraftMin, setQtyReceivedInDraftMin] = useState(savedFilters?.qtyReceivedInDraftMin ?? '');
    const [qtyReceivedInDraftMax, setQtyReceivedInDraftMax] = useState(savedFilters?.qtyReceivedInDraftMax ?? '');
    const [qtySubmittedMin, setQtySubmittedMin] = useState(savedFilters?.qtySubmittedMin ?? '');
    const [qtySubmittedMax, setQtySubmittedMax] = useState(savedFilters?.qtySubmittedMax ?? '');

    // ====== FILTER OPTIONS STATE ======
    const [vendorOptions, setVendorOptions] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [baseLocationOptions, setBaseLocationOptions] = useState([]);
    const [statusOptions] = useState(STATUS_OPTIONS);

    // Always show all assigned locations for the user in the filter menu
    const locationOptions = useMemo(() => {
        if (!currentUser?.locations) return [];
        return currentUser.locations.map((loc, idx) => ({
            label: currentUser.locationNames?.[idx] || loc.locationId,
            value: loc.locationId,
        }));
    }, [currentUser?.locations, currentUser?.locationNames]);

    // Debounced values for quantity filters
    const debouncedQtyOrderedMin = useDebounce(qtyOrderedMin);
    const debouncedQtyOrderedMax = useDebounce(qtyOrderedMax);
    const debouncedQtyReceivedInDraftMin = useDebounce(qtyReceivedInDraftMin);
    const debouncedQtyReceivedInDraftMax = useDebounce(qtyReceivedInDraftMax);
    const debouncedQtySubmittedMin = useDebounce(qtySubmittedMin);
    const debouncedQtySubmittedMax = useDebounce(qtySubmittedMax);
    // ====== INDEX FILTERS MODE ======
    const { setMode } = useSetIndexFiltersMode();
    const tabs = [];

    // ====== SAVE FILTERS TO SESSION STORAGE ======
    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return; // ⛔️ skip initial save to sessionStorage
        }
        const filters = {
            queryValue,
            poNumberFilter,
            vendorFilter,
            statusFilter,
            locationFilter,
            startDateFilter,
            endDateFilter,
            expectedShipDateFromFilter,
            expectedShipDateToFilter,
            cancelDateFromFilter,
            cancelDateToFilter,
            qtyOrderedMin,
            qtyOrderedMax,
            qtyReceivedMin,
            qtyReceivedMax,
            sortSelected,
            currentPage,
        };
        saveFiltersToSession(filters);
    }, [
        queryValue,
        poNumberFilter,
        vendorFilter,
        statusFilter,
        locationFilter,
        startDateFilter,
        endDateFilter,
        expectedShipDateFromFilter,
        expectedShipDateToFilter,
        cancelDateFromFilter,
        cancelDateToFilter,
        qtyOrderedMin,
        qtyOrderedMax,
        qtyReceivedMin,
        qtyReceivedMax,
        sortSelected,
        currentPage,
    ]);

    // ====== FETCH FILTER OPTIONS ======
    const fetchFilterOptions = useCallback(async () => {
        try {
            const response = await purchaseOrderService.fetchUniqueFilterOptions('purchaseOrder', 'vendor');

            let uniqueVendors = response.data.data;

            let vendorOptions = uniqueVendors.map((vendor) => ({
                label: vendor,
                value: vendor,
            }));

            setVendorOptions(vendorOptions);
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    }, []);

    // Initial fetch of filter options
    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    // ====== HANDLE SELECTED LOCATION CHANGE ======

    // ====== FILTER HANDLERS ======

    const handleFiltersQueryChange = useCallback((value) => {
        setQueryValue(value);
    }, []);

    const handlePoNumberFilterChange = useCallback((value) => {
        setPoNumberFilter(value);
    }, []);

    const handleVendorFilterChange = useCallback((value) => {
        setVendorFilter(value);
    }, []);

    const handleStatusFilterChange = useCallback((value) => {
        setStatusFilter(value);
    }, []);

    const handleLocationFilterChange = useCallback((value) => {
        setLocationFilter(value);
    }, []);

    const handleEndDateFilterChange = useCallback((value) => {
        setEndDateFilter(value);
    }, []);

    const handleExpectedShipDateToFilterChange = useCallback((value) => {
        setExpectedShipDateToFilter(value);
    }, []);

    const handleCancelDateToFilterChange = useCallback((value) => {
        setCancelDateToFilter(value);
    }, []);

    const handleQtyOrderedMinChange = useCallback((value) => {
        setQtyOrderedMin(value);
    }, []);

    const handleQtyOrderedMaxChange = useCallback((value) => {
        setQtyOrderedMax(value);
    }, []);

    const handleQtyReceivedInDraftMinChange = useCallback((value) => {
        setQtyReceivedInDraftMin(value);
    }, []);

    const handleQtyReceivedInDraftMaxChange = useCallback((value) => {
        setQtyReceivedInDraftMax(value);
    }, []);

    const handleQtySubmittedMinChange = useCallback((value) => {
        setQtySubmittedMin(value);
    }, []);

    const handleQtySubmittedMaxChange = useCallback((value) => {
        setQtySubmittedMax(value);
    }, []);

    const handleStartDateFilterChange = useCallback((value) => {
        setStartDateFilter(value);
    }, []);

    const handleExpectedShipDateFromFilterChange = useCallback((value) => {
        setExpectedShipDateFromFilter(value);
    }, []);

    const handleCancelDateFromFilterChange = useCallback((value) => {
        setCancelDateFromFilter(value);
    }, []);

    // Search field handlers

    const handleSearchFieldCancelButtonClick = () => {
        setQueryValue('');
    };

    // Filter removal handlers
    const handleQueryValueRemove = useCallback(() => setQueryValue(''), []);
    const handlePoNumberFilterRemove = useCallback(() => setPoNumberFilter(''), []);
    const handleVendorFilterRemove = useCallback(() => setVendorFilter([]), []);
    const handleStatusFilterRemove = useCallback(() => setStatusFilter([]), []);
    const handleLocationFilterRemove = useCallback(() => setLocationFilter([]), []);
    const handleStartDateRangeRemove = useCallback(() => {
        setStartDateFilter('');
        setEndDateFilter('');
    }, []);
    const handleExpectedShipDateRangeRemove = useCallback(() => {
        setExpectedShipDateFromFilter('');
        setExpectedShipDateToFilter('');
    }, []);
    const handleCancelDateRangeRemove = useCallback(() => {
        setCancelDateFromFilter('');
        setCancelDateToFilter('');
    }, []);

    const handleQtyOrderedMinRemove = useCallback(() => setQtyOrderedMin(''), []);
    const handleQtyOrderedMaxRemove = useCallback(() => setQtyOrderedMax(''), []);
    const handleQtyReceivedMinRemove = useCallback(() => setQtyReceivedMin(''), []);
    const handleQtyReceivedMaxRemove = useCallback(() => setQtyReceivedMax(''), []);
    const handleQtyReceivedInDraftMinRemove = useCallback(() => setQtyReceivedInDraftMin(''), []);
    const handleQtyReceivedInDraftMaxRemove = useCallback(() => setQtyReceivedInDraftMax(''), []);
    const handleQtySubmittedMinRemove = useCallback(() => setQtySubmittedMin(''), []);
    const handleQtySubmittedMaxRemove = useCallback(() => setQtySubmittedMax(''), []);
    const handleFiltersClearAll = useCallback(() => {
        handleQueryValueRemove();
        handlePoNumberFilterRemove();
        handleVendorFilterRemove();
        handleStatusFilterRemove();
        handleLocationFilterRemove();
        handleStartDateRangeRemove();
        handleExpectedShipDateRangeRemove();
        handleCancelDateRangeRemove();
        handleQtyOrderedMinRemove();
        handleQtyOrderedMaxRemove();
        handleQtyReceivedMinRemove();
        handleQtyReceivedMaxRemove();
        handleQtyReceivedInDraftMinRemove();
        handleQtyReceivedInDraftMaxRemove();
        handleQtySubmittedMinRemove();
        handleQtySubmittedMaxRemove();
        // Also clear session storage when clearing all filters
        sessionStorage.removeItem(PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.FILTER_STORAGE_KEY);
    }, [
        handleQueryValueRemove,
        handlePoNumberFilterRemove,
        handleVendorFilterRemove,
        handleStatusFilterRemove,
        handleLocationFilterRemove,
        handleStartDateRangeRemove,
        handleExpectedShipDateRangeRemove,
        handleCancelDateRangeRemove,
        handleQtyOrderedMinRemove,
        handleQtyOrderedMaxRemove,
        handleQtyReceivedMinRemove,
        handleQtyReceivedMaxRemove,
        currentUser?.activeStore,
    ]);

    // ====== PAGINATION HANDLERS ======

    const handleNextPage = useCallback(() => {
        setCurrentPage((prevPage) => prevPage + 1);
    }, []);

    const handlePreviousPage = useCallback(() => {
        setCurrentPage((prevPage) => Math.max(1, prevPage - 1));
    }, []);

    // ====== UTILITY FUNCTIONS ======

    /**
     * Helper function to check if a value is empty
     */
    const isEmpty = (value) => {
        if (Array.isArray(value)) {
            return value.length === 0;
        } else {
            return value === '' || value == null;
        }
    };

    /**
     * Create human-readable labels for applied filters
     */
    const disambiguateLabel = (key, value) => {
        switch (key) {
            case PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER:
                return `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER_TITLE}: ${value}`;
            case PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR:
                return `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR_TITLE}: ${Array.isArray(value) ? value.join(', ') : value}`;
            case PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.LOCATION:
                // Convert location GIDs to names for display
                if (Array.isArray(value)) {
                    const locationNames = value.map((gid) => {
                        const location = locationOptions.find((opt) => opt.value === gid);
                        return location ? location.label : gid;
                    });
                    return `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION_TITLE}: ${locationNames.join(', ')}`;
                }
                return `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION_TITLE}: ${value}`;
            case PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS:
                return `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS_TITLE}: ${value.join(', ')}`;
            case PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION:
                // Convert location GIDs to names for display
                if (Array.isArray(value)) {
                    const locationNames = value.map((gid) => {
                        const location = locationOptions.find((opt) => opt.value === gid);
                        return location ? location.label : gid;
                    });
                    return `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION_TITLE}: ${locationNames.join(', ')}`;
                }
                return `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION_TITLE}: ${value}`;
            default:
                return value;
        }
    };

    /**
     * Parse sort value to get column index and direction for API
     */
    const parseSortValue = (sortValue) => {
        const sortMap = {
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER_ASC_VALUE]: { index: 0, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER_DESC_VALUE]: { index: 0, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR_ASC_VALUE]: { index: 1, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR_DESC_VALUE]: { index: 1, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION_ASC_VALUE]: { index: 2, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION_DESC_VALUE]: { index: 2, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS_ASC_VALUE]: { index: 3, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS_DESC_VALUE]: { index: 3, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_ASC_VALUE]: { index: 4, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_DESC_VALUE]: { index: 4, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_ASC_VALUE]: { index: 5, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_DESC_VALUE]: { index: 5, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_ASC_VALUE]: { index: 6, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_DESC_VALUE]: { index: 6, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_ASC_VALUE]: { index: 8, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_DESC_VALUE]: { index: 8, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_ASC_VALUE]: { index: 10, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_DESC_VALUE]: { index: 10, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_ASC_VALUE]: { index: 11, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.ASC },
            [PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_DESC_VALUE]: { index: 11, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC },
        };
        return sortMap[sortValue] || { index: 4, direction: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESC };
    };

    /**
     * Calculate pagination values
     */
    const calculatePagination = () => {
        const totalPages = Math.ceil(dataState.total / itemsPerPage);
        const hasNext = currentPage < totalPages;
        const hasPrevious = currentPage > 1;

        return {
            hasNext,
            hasPrevious,
            totalPages,
        };
    };

    // ====== API FUNCTIONS ======

    /**
     * Fetches purchase order data from API using the service
     */
    const fetchPurchaseOrderData = async (page) => {
        setDataState((prev) => ({ ...prev, isLoading: true }));

        try {
            // Build filters object from current state
            const filters = {
                queryValue,
                poNumberFilter,
                vendorFilter,
                statusFilter,
                locationFilter,
                startDateFilter,
                endDateFilter,
                expectedShipDateFromFilter,
                expectedShipDateToFilter,
                cancelDateFromFilter,
                cancelDateToFilter,
                qtyOrderedMin: debouncedQtyOrderedMin,
                qtyOrderedMax: debouncedQtyOrderedMax,
                qtyReceivedInDraftMin: debouncedQtyReceivedInDraftMin,
                qtyReceivedInDraftMax: debouncedQtyReceivedInDraftMax,
                qtySubmittedMin: debouncedQtySubmittedMin,
                qtySubmittedMax: debouncedQtySubmittedMax,
            };

            // Add sorting parameters
            if (sortSelected.length > 0) {
                const { index, direction } = parseSortValue(sortSelected[0]);
                filters.sortColumn = index;
                filters.sortDirection = direction;
            }

            const result = await purchaseOrderService.fetchPaginatedPurchaseOrders(page, itemsPerPage, filters);

            setDataState({
                rows: result.rows,
                total: result.total,
                isLoading: false,
            });
        } catch (error) {
            setDataState({
                rows: [],
                total: 0,
                isLoading: false,
            });
        }
    };

    // ====== EFFECTS ======
    const didMountRef = useRef(false);
    const previousFiltersHashRef = useRef(null);
    const isInitialMount = useRef(true);

    // Create a hash for array values
    const getArrayHash = useCallback((arr) => {
        if (!Array.isArray(arr)) return arr;
        return arr.join('|');
    }, []);

    // Memoize filter configuration
    const FILTER_CONFIG = useMemo(
        () => ({
            queryValue: false,
            poNumberFilter: false,
            vendorFilter: false,
            statusFilter: true,
            locationFilter: false,
            startDateFilter: false,
            endDateFilter: false,
            expectedShipDateFromFilter: false,
            expectedShipDateToFilter: false,
            cancelDateFromFilter: false,
            cancelDateToFilter: false,
            qtyOrderedMin: false,
            qtyOrderedMax: false,
            qtyReceivedInDraftMin: false,
            qtyReceivedInDraftMax: false,
            qtySubmittedMin: false,
            qtySubmittedMax: false,
            sortSelected: true,
        }),
        []
    );

    // Memoize current filters object
    const currentFilters = useMemo(
        () => ({
            queryValue,
            poNumberFilter,
            vendorFilter,
            statusFilter,
            locationFilter,
            startDateFilter,
            endDateFilter,
            expectedShipDateFromFilter,
            expectedShipDateToFilter,
            cancelDateFromFilter,
            cancelDateToFilter,
            qtyOrderedMin: debouncedQtyOrderedMin,
            qtyOrderedMax: debouncedQtyOrderedMax,
            qtyReceivedInDraftMin: debouncedQtyReceivedInDraftMin,
            qtyReceivedInDraftMax: debouncedQtyReceivedInDraftMax,
            qtySubmittedMin: debouncedQtySubmittedMin,
            qtySubmittedMax: debouncedQtySubmittedMax,
            sortSelected,
        }),
        [
            queryValue,
            poNumberFilter,
            vendorFilter,
            statusFilter,
            locationFilter,
            startDateFilter,
            endDateFilter,
            expectedShipDateFromFilter,
            expectedShipDateToFilter,
            cancelDateFromFilter,
            cancelDateToFilter,
            debouncedQtyOrderedMin,
            debouncedQtyOrderedMax,
            debouncedQtyReceivedInDraftMin,
            debouncedQtyReceivedInDraftMax,
            debouncedQtySubmittedMin,
            debouncedQtySubmittedMax,
            sortSelected,
        ]
    );

    // Create hash map of current filter values
    const currentFilterHash = useMemo(() => {
        const hash = {};
        for (const [key, isArray] of Object.entries(FILTER_CONFIG)) {
            hash[key] = isArray ? getArrayHash(currentFilters[key]) : currentFilters[key];
        }
        return hash;
    }, [currentFilters, FILTER_CONFIG, getArrayHash]);

    // Reset page on filter change (after mount)
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            previousFiltersHashRef.current = currentFilterHash;
            return;
        }

        // Check if filters changed from previous render
        const prevHash = previousFiltersHashRef.current;
        let hasChanged = false;

        for (const key in currentFilterHash) {
            if (currentFilterHash[key] !== prevHash[key]) {
                hasChanged = true;
                break;
            }
        }

        // Update the ref for next comparison
        previousFiltersHashRef.current = currentFilterHash;

        // Reset page if filters changed
        if (hasChanged) {
            setCurrentPage(1);
            queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
        }
    }, [currentFilterHash]);

    // Refetch data when active location changes
    useEffect(() => {
        if (currentUser?.activeStore !== undefined) {
            fetchPurchaseOrderData(1);
            setCurrentPage(1);
        }
    }, [currentUser?.activeStore]);

    // Debounced data fetch
    useEffect(() => {
        const debounceDelay = isInitialMount.current ? 0 : 250; // This is used when moving back to the po dashboard from a detail page. It gives it time to grab the data

        const debounceTimer = setTimeout(() => {
            fetchPurchaseOrderData(currentPage);
            if (isInitialMount.current) {
                isInitialMount.current = false;
            }
        }, debounceDelay);

        return () => clearTimeout(debounceTimer);
    }, [currentPage, currentFilters]);

    // Validate current page
    useEffect(() => {
        if (dataState.total > 0 && !dataState.isLoading) {
            const maxPage = Math.ceil(dataState.total / itemsPerPage);
            if (currentPage > maxPage) {
                setCurrentPage(1);
                isInitialMount.current = true;
            }
        }
    }, [dataState.total, dataState.isLoading, currentPage, itemsPerPage]);

    // Breakpoints
    const breakpoints = useBreakpoints();

    // ====== FILTER CONFIGURATION ======
    const filters = [
        {
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER,
            label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER_TITLE,
            filter: (
                <div className="purchase-order-dashboard-filters-text-width">
                    <TextField
                        label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER_TITLE}
                        value={poNumberFilter}
                        onChange={handlePoNumberFilterChange}
                        autoComplete="off"
                        labelHidden
                        placeholder={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.FILTER_BY_PO_NUMBER}
                    />
                </div>
            ),
            shortcut: true,
        },
        {
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR,
            label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR_TITLE,
            filter: (
                <ChoiceList
                    title={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR_TITLE}
                    titleHidden
                    choices={vendorOptions}
                    selected={Array.isArray(vendorFilter) ? vendorFilter : []}
                    onChange={handleVendorFilterChange}
                    allowMultiple
                />
            ),
            shortcut: true,
        },
        {
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.LOCATION,
            label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION,
            filter: (
                <ChoiceList
                    title={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION}
                    titleHidden
                    choices={locationOptions}
                    selected={currentUser.activeStore && currentUser.activeStore.length > 0 ? currentUser.activeStore : Array.isArray(locationFilter) ? locationFilter : ''}
                    onChange={handleLocationFilterChange}
                    allowMultiple
                    disabled={currentUser.activeStore && currentUser.activeStore.length > 0}
                />
            ),
            shortcut: true,
        },
        {
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS,
            label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS_TITLE,
            filter: (
                <ChoiceList
                    title={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS_TITLE}
                    titleHidden
                    choices={statusOptions}
                    selected={Array.isArray(statusFilter) ? statusFilter : []}
                    onChange={handleStatusFilterChange}
                    allowMultiple
                />
            ),
            shortcut: true,
        },
        {
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_RANGE_KEY,
            label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_TITLE,
            filter: (
                <div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={startDateFilter} onChange={handleStartDateFilterChange} autoComplete="off" type="date" />
                    </div>
                    <div className="purchase-order-dashboard-filters-date-text">{PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO}</div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={endDateFilter} onChange={handleEndDateFilterChange} autoComplete="off" type="date" />
                    </div>
                </div>
            ),
            shortcut: true,
        },
        {
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_RANGE_KEY,
            label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_TITLE,
            filter: (
                <div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={expectedShipDateFromFilter} onChange={handleExpectedShipDateFromFilterChange} autoComplete="off" type="date" />
                    </div>
                    <div className="purchase-order-dashboard-filters-date-text">{PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO}</div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={expectedShipDateToFilter} onChange={handleExpectedShipDateToFilterChange} autoComplete="off" type="date" />
                    </div>
                </div>
            ),
            shortcut: true,
        },
        {
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_RANGE_KEY,
            label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_TITLE,
            filter: (
                <div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={cancelDateFromFilter} onChange={handleCancelDateFromFilterChange} autoComplete="off" type="date" />
                    </div>
                    <div className="purchase-order-dashboard-filters-date-text">{PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO}</div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField value={cancelDateToFilter} onChange={handleCancelDateToFilterChange} autoComplete="off" type="date" />
                    </div>
                </div>
            ),
            shortcut: true,
        },
        {
            key: 'qtyOrderedRange',
            label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_TITLE,
            filter: (
                <div className="purchase-order-dashboard-filter-wrapper">
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField
                            label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_MIN}
                            value={qtyOrderedMin}
                            onChange={handleQtyOrderedMinChange}
                            autoComplete="off"
                            labelHidden
                            placeholder="Min"
                            type="number"
                        />
                    </div>
                    <div className="purchase-order-dashboard-filters-date-text">{PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO}</div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField
                            label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_MAX}
                            value={qtyOrderedMax}
                            onChange={handleQtyOrderedMaxChange}
                            autoComplete="off"
                            labelHidden
                            placeholder="Max"
                            type="number"
                        />
                    </div>
                </div>
            ),
        },
        {
            key: 'qtyReceivedInDraftRange',
            label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_TITLE,
            filter: (
                <div className="purchase-order-dashboard-filter-wrapper">
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField
                            label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_MIN}
                            value={qtyReceivedInDraftMin}
                            onChange={handleQtyReceivedInDraftMinChange}
                            autoComplete="off"
                            labelHidden
                            placeholder="Min"
                            type="number"
                        />
                    </div>
                    <div className="purchase-order-dashboard-filters-date-text">{PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO}</div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField
                            label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_MAX}
                            value={qtyReceivedInDraftMax}
                            onChange={handleQtyReceivedInDraftMaxChange}
                            autoComplete="off"
                            labelHidden
                            placeholder="Max"
                            type="number"
                        />
                    </div>
                </div>
            ),
        },
        {
            key: 'qtySubmittedRange',
            label: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_TITLE,
            filter: (
                <div className="purchase-order-dashboard-filter-wrapper">
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField
                            label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_MIN}
                            value={qtySubmittedMin}
                            onChange={handleQtySubmittedMinChange}
                            autoComplete="off"
                            labelHidden
                            placeholder="Min"
                            type="number"
                        />
                    </div>
                    <div className="purchase-order-dashboard-filters-date-text">{PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO}</div>
                    <div className="purchase-order-dashboard-filters-flex-1">
                        <TextField
                            label={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_MAX}
                            value={qtySubmittedMax}
                            onChange={handleQtySubmittedMaxChange}
                            autoComplete="off"
                            labelHidden
                            placeholder="Max"
                            type="number"
                        />
                    </div>
                </div>
            ),
        },
    ];

    // ====== APPLIED FILTERS ======

    const appliedFilters = [];

    if (poNumberFilter && !isEmpty(poNumberFilter)) {
        appliedFilters.push({
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER,
            label: disambiguateLabel(PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PO_NUMBER, poNumberFilter),
            onRemove: handlePoNumberFilterRemove,
        });
    }

    if (vendorFilter && !isEmpty(vendorFilter)) {
        appliedFilters.push({
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR,
            label: disambiguateLabel(PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VENDOR, vendorFilter),
            onRemove: handleVendorFilterRemove,
        });
    }

    if (statusFilter && !isEmpty(statusFilter)) {
        appliedFilters.push({
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS,
            label: disambiguateLabel(PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.STATUS, statusFilter),
            onRemove: handleStatusFilterRemove,
        });
    }

    if (locationFilter && !isEmpty(locationFilter)) {
        appliedFilters.push({
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.LOCATION,
            label: disambiguateLabel(PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.DESTINATION_LOCATION, locationFilter),
            onRemove: handleLocationFilterRemove,
        });
    }

    // GROUPED DATE RANGE APPLIED FILTERS
    if ((startDateFilter && !isEmpty(startDateFilter)) || (endDateFilter && !isEmpty(endDateFilter))) {
        const dateRangeLabel = [];
        if (startDateFilter && endDateFilter) {
            dateRangeLabel.push(`${startDateFilter} to ${endDateFilter}`);
        } else if (startDateFilter) {
            dateRangeLabel.push(`${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.FROM} ${startDateFilter}`);
        } else if (endDateFilter) {
            dateRangeLabel.push(`${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO} ${endDateFilter}`);
        }

        appliedFilters.push({
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_RANGE_KEY,
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.START_DATE_TITLE}: ${dateRangeLabel.join('')}`,
            onRemove: handleStartDateRangeRemove,
        });
    }

    if ((expectedShipDateFromFilter && !isEmpty(expectedShipDateFromFilter)) || (expectedShipDateToFilter && !isEmpty(expectedShipDateToFilter))) {
        const dateRangeLabel = [];
        if (expectedShipDateFromFilter && expectedShipDateToFilter) {
            dateRangeLabel.push(`${expectedShipDateFromFilter} to ${expectedShipDateToFilter}`);
        } else if (expectedShipDateFromFilter) {
            dateRangeLabel.push(`${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.FROM} ${expectedShipDateFromFilter}`);
        } else if (expectedShipDateToFilter) {
            dateRangeLabel.push(`${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO} ${expectedShipDateToFilter}`);
        }

        appliedFilters.push({
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_RANGE_KEY,
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EXPECTED_SHIP_DATE_TITLE}: ${dateRangeLabel.join('')}`,
            onRemove: handleExpectedShipDateRangeRemove,
        });
    }

    if ((cancelDateFromFilter && !isEmpty(cancelDateFromFilter)) || (cancelDateToFilter && !isEmpty(cancelDateToFilter))) {
        const dateRangeLabel = [];
        if (cancelDateFromFilter && cancelDateToFilter) {
            dateRangeLabel.push(`${cancelDateFromFilter} ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO} ${cancelDateToFilter}`);
        } else if (cancelDateFromFilter) {
            dateRangeLabel.push(`${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.FROM} ${cancelDateFromFilter}`);
        } else if (cancelDateToFilter) {
            dateRangeLabel.push(`${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO} ${cancelDateToFilter}`);
        }

        appliedFilters.push({
            key: PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_RANGE_KEY,
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.CANCEL_DATE_TITLE}: ${dateRangeLabel.join('')}`,
            onRemove: handleCancelDateRangeRemove,
        });
    }

    if (qtyOrderedMin && !isEmpty(qtyOrderedMin) && qtyOrderedMax && !isEmpty(qtyOrderedMax)) {
        appliedFilters.push({
            key: 'qtyOrderedRange',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_TITLE}: ${qtyOrderedMin} ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO} ${qtyOrderedMax}`,
            onRemove: () => {
                handleQtyOrderedMinRemove();
                handleQtyOrderedMaxRemove();
            },
        });
    } else if (qtyOrderedMin && !isEmpty(qtyOrderedMin)) {
        appliedFilters.push({
            key: 'qtyOrderedRange',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_TITLE}: ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.MIN} ${qtyOrderedMin}`,
            onRemove: handleQtyOrderedMinRemove,
        });
    } else if (qtyOrderedMax && !isEmpty(qtyOrderedMax)) {
        appliedFilters.push({
            key: 'qtyOrderedRange',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_ORDERED_TITLE}: ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.MAX} ${qtyOrderedMax}`,
            onRemove: handleQtyOrderedMaxRemove,
        });
    }

    if (qtyReceivedMin && !isEmpty(qtyReceivedMin) && qtyReceivedMax && !isEmpty(qtyReceivedMax)) {
        appliedFilters.push({
            key: 'qtyReceivedRange',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_TITLE}: ${qtyReceivedMin} ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO} ${qtyReceivedMax}`,
            onRemove: () => {
                handleQtyReceivedMinRemove();
                handleQtyReceivedMaxRemove();
            },
        });
    } else if (qtyReceivedMin && !isEmpty(qtyReceivedMin)) {
        appliedFilters.push({
            key: 'qtyReceivedMin',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_TITLE}: ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.MIN} ${qtyReceivedMin}`,
            onRemove: handleQtyReceivedMinRemove,
        });
    } else if (qtyReceivedMax && !isEmpty(qtyReceivedMax)) {
        appliedFilters.push({
            key: 'qtyReceivedMax',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_TITLE}: ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.MAX} ${qtyReceivedMax}`,
            onRemove: handleQtyReceivedMaxRemove,
        });
    }
    if (qtyReceivedInDraftMin && !isEmpty(qtyReceivedInDraftMin) && qtyReceivedInDraftMax && !isEmpty(qtyReceivedInDraftMax)) {
        appliedFilters.push({
            key: 'qtyReceivedInDraftRange',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_TITLE}: ${qtyReceivedInDraftMin} ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO} ${qtyReceivedInDraftMax}`,
            onRemove: () => {
                handleQtyReceivedInDraftMinRemove();
                handleQtyReceivedInDraftMaxRemove();
            },
        });
    } else if (qtyReceivedInDraftMin && !isEmpty(qtyReceivedInDraftMin)) {
        appliedFilters.push({
            key: 'qtyReceivedInDraftRange',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_TITLE}: ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.MIN} ${qtyReceivedInDraftMin}`,
            onRemove: handleQtyReceivedInDraftMinRemove,
        });
    } else if (qtyReceivedInDraftMax && !isEmpty(qtyReceivedInDraftMax)) {
        appliedFilters.push({
            key: 'qtyReceivedInDraftRange',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_RECEIVED_IN_DRAFT_TITLE}: ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.MAX} ${qtyReceivedInDraftMax}`,
            onRemove: handleQtyReceivedInDraftMaxRemove,
        });
    }
    if (qtySubmittedMin && !isEmpty(qtySubmittedMin) && qtySubmittedMax && !isEmpty(qtySubmittedMax)) {
        appliedFilters.push({
            key: 'qtySubmittedRange',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_TITLE}: ${qtySubmittedMin} ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.TO} ${qtySubmittedMax}`,
            onRemove: () => {
                handleQtySubmittedMinRemove();
                handleQtySubmittedMaxRemove();
            },
        });
    } else if (qtySubmittedMin && !isEmpty(qtySubmittedMin)) {
        appliedFilters.push({
            key: 'qtySubmittedRange',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_TITLE}: ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.MIN} ${qtySubmittedMin}`,
            onRemove: handleQtySubmittedMinRemove,
        });
    } else if (qtySubmittedMax && !isEmpty(qtySubmittedMax)) {
        appliedFilters.push({
            key: 'qtySubmittedRange',
            label: `${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.QTY_SUBMITTED_TITLE}: ${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.MAX} ${qtySubmittedMax}`,
            onRemove: handleQtySubmittedMaxRemove,
        });
    }
    // Generate table headings from column config
    const TABLE_HEADINGS = COLUMN_CONFIG.map((column) => ({
        title: column.title,
    }));

    // ====== TABLE ROW MARKUP ======

    const rowMarkup = dataState.rows.map((order, index) => (
        <IndexTable.Row id={order.id} key={order.id} position={index}>
            <IndexTable.Cell>
                <Text variant="bodyMd" fontWeight="semibold" as="span">
                    {order.netSuiteHumanPoNumber}
                </Text>
            </IndexTable.Cell>
            <IndexTable.Cell>{order.vendor}</IndexTable.Cell>
            <IndexTable.Cell>
                <StoreLocation locationId={order.location} />
            </IndexTable.Cell>
            <IndexTable.Cell>
                <CustomBadge status={order.status} />
            </IndexTable.Cell>
            <IndexTable.Cell>{formatDateToUTC(order.startDate)}</IndexTable.Cell>
            <IndexTable.Cell>{formatDateToUTC(order.expectedShipDate)}</IndexTable.Cell>
            <IndexTable.Cell>{formatDateToUTC(order.cancelDate)}</IndexTable.Cell>
            <IndexTable.Cell>
                <Text as="span">{order.qtyOrdered}</Text>
            </IndexTable.Cell>
            <IndexTable.Cell>
                <Text as="span">{order.qtyReceivedInDraft}</Text>
            </IndexTable.Cell>

            <IndexTable.Cell>
                <Text as="span">{order.qtySubmitted}</Text>
            </IndexTable.Cell>

            <IndexTable.Cell>
                <Button
                    icon={ViewIcon}
                    variant="plain"
                    onClick={() => navigate(`/purchase-orders/${order.id}`)}
                    accessibilityLabel={`${PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.VIEW_PURCHASE_ORDER} ${order.netSuiteHumanPoNumber}`}
                />
            </IndexTable.Cell>
        </IndexTable.Row>
    ));

    // ====== EMPTY STATE MARKUP ======

    const emptyStateMarkup = (
        <EmptySearchResult
            title={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EMPTY_STATE_MARKUP_TITLE}
            description={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.EMPTY_STATE_MARKUP_DESCRIPTION}
            withIllustration
        />
    );

    // ====== PAGINATION INFO ======

    const { hasNext, hasPrevious } = calculatePagination();

    // ====== RENDER ======
    const startRow = (currentPage - 1) * itemsPerPage + 1;
    const endRow = Math.min(currentPage * itemsPerPage, dataState.total);
    const paginationLabel = `${startRow} - ${endRow} of ${dataState.total}`;

    return (
        <div className="purchase-order-dashboard">
            <Page fullWidth>
                <Layout>
                    <Layout.Section>
                        <Card>
                            <Box paddingBlock="400">
                                <Text variant="headingLg">{PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.PURCHASE_ORDERS_PANEL_TITLE}</Text>
                            </Box>
                            <div style={{ display: 'flex' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ padding: '8px', display: 'flex', justifyContent: 'flex-end' }}>{/*<ExportButton type="PurchaseOrder" filters={currentFilters} />*/}</div>
                                    <IndexFilters
                                        sortOptions={SORT_OPTIONS}
                                        sortSelected={sortSelected}
                                        queryValue={queryValue}
                                        queryPlaceholder={PURCHASE_ORDER_STRINGS.PURCHASE_ORDERS_DASHBOARD.SEARCH_TITLE}
                                        onQueryChange={handleFiltersQueryChange}
                                        onQueryClear={handleQueryValueRemove}
                                        onSort={setSortSelected}
                                        filters={filters}
                                        appliedFilters={appliedFilters}
                                        onClearAll={handleFiltersClearAll}
                                        mode="FILTERING"
                                        setMode={setMode}
                                        tabs={tabs}
                                        selected={0}
                                        onSelect={() => {}}
                                        canCreateNewView={false}
                                        cancelAction={{
                                            onAction: handleSearchFieldCancelButtonClick,
                                            disabled: false,
                                            loading: false, // spinner handled manually
                                        }}
                                        autoFocusSearchField={false}
                                    />
                                </div>

                                {dataState.isLoading && (
                                    <div style={{ padding: '12px' }}>
                                        <Spinner accessibilityLabel="Loading purchase orders" size="small" />
                                    </div>
                                )}
                            </div>
                            <IndexTable
                                condensed={breakpoints.smDown}
                                emptyState={emptyStateMarkup}
                                resourceName={resourceName}
                                itemCount={dataState.rows.length}
                                headings={TABLE_HEADINGS}
                                selectable={false}
                                pagination={{
                                    hasPrevious,
                                    hasNext,
                                    onNext: handleNextPage,
                                    onPrevious: handlePreviousPage,
                                    label: paginationLabel,
                                }}
                            >
                                {rowMarkup}
                            </IndexTable>
                        </Card>
                    </Layout.Section>
                    <Layout.Section>
                        <Card>
                            <ActivityLog objectType="PurchaseOrder" />
                        </Card>
                    </Layout.Section>
                </Layout>
            </Page>
        </div>
    );
}
